import { useMutation, useQuery } from "@apollo/client";
import { Stack, Typography } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FeatureFlagKey, KNOWN_FEATURE_FLAG_KEYS } from "encamp-shared/src/featureFlags";
import { type GetFlagMeta, useFeatureFlagMeta } from "featureFlags/useFeatureFlagMeta";
import { useMemo, useState } from "react";

import {
  collectDependencyKeysToActivate,
  collectDependencyKeysToDeactivate,
  FeatureFlagTogglePanel,
  FlagState,
} from "../../../../../components/FeatureFlags/FeatureFlagTogglePanel";
import { gql } from "../../../../../generated-graphql";

const TENANT_FLAGS_QUERY = gql(`
  query TenantFeatureFlags($tenantId: ID!) {
    tenant(id: $tenantId) {
      id
      name
      tenantConfigs {
        id
        tenantId
        key
        value
      }
    }
  }
`);

// Bulk mutation: writes all updates atomically inside one prisma
// transaction on the server, so a failed cascaded toggle aborts the
// whole batch instead of leaving the tenant half-updated. The
// `setTenantFeatureFlag` (singular) RPC is still in the schema but we
// route every staff write through the bulk endpoint to keep the
// transaction guarantee.
const BULK_SET_FLAGS_MUTATION = gql(`
  mutation BulkSetTenantFeatureFlagsForTenant($inputs: [SetTenantFeatureFlagInput!]!) {
    setTenantFeatureFlags(inputs: $inputs) {
      id
      tenantConfigs {
        id
        tenantId
        key
        value
      }
    }
  }
`);

type PendingUpdate = { key: FeatureFlagKey; value: boolean };

type TenantConfigFlagValue = {
  key: string;
  value: boolean;
};

export function computeCurrentStatesForTenant(
  tenantConfigs: readonly TenantConfigFlagValue[],
  getMeta: GetFlagMeta
): Partial<Record<FeatureFlagKey, FlagState>> {
  const explicitByKey = new Map(tenantConfigs.map((config) => [config.key, config.value]));
  const states: Partial<Record<FeatureFlagKey, FlagState>> = {};
  for (const key of KNOWN_FEATURE_FLAG_KEYS) {
    states[key] = explicitByKey.get(key) ?? getMeta(key).defaultValue;
  }
  return states;
}

export function FeatureFlagsTab({ tenantId }: { tenantId: string }) {
  const { data, loading } = useQuery(TENANT_FLAGS_QUERY, {
    variables: { tenantId },
    fetchPolicy: "cache-and-network",
  });
  const [bulkSetFlags] = useMutation(BULK_SET_FLAGS_MUTATION);
  const alerts = useAlerts();
  // When a user toggles Measurements (directly or via a cascade), stash
  // the full batch here and show a confirmation dialog. Only once they
  // confirm do we actually fire the mutation. `null` means no dialog.
  const [pendingConfirmation, setPendingConfirmation] = useState<PendingUpdate[] | null>(null);
  const { getMeta } = useFeatureFlagMeta();

  const currentStates = useMemo(
    () => computeCurrentStatesForTenant(data?.tenant?.tenantConfigs ?? [], getMeta),
    [data?.tenant?.tenantConfigs, getMeta]
  );

  /**
   * Apply a batch of key→value updates as a single atomic bulk
   * mutation. The server wraps the whole batch in one
   * `prisma.$transaction`, so a failed cascaded toggle aborts the
   * entire batch rather than leaving the tenant half-updated. The
   * optimistic response carries the merged-final `tenantConfigs` shape
   * the cache should reflect once the mutation resolves, so the UI
   * stays consistent across the in-flight window.
   */
  const performToggles = async (updates: PendingUpdate[]) => {
    if (updates.length === 0) return;

    // `Map<string, boolean>` mirrors the GraphQL schema's `String!`
    // typing for `TenantConfig.key`; widening here lets us call
    // `byKey.has(c.key)` directly without a cast.
    const byKey = new Map<string, boolean>(updates.map((u) => [u.key, u.value]));
    const baseConfigs = data?.tenant?.tenantConfigs ?? [];
    const finalConfigs = [
      ...baseConfigs.filter((c) => !byKey.has(c.key)),
      ...updates.map((u) => ({
        __typename: "TenantConfig" as const,
        id: `temp-${tenantId}-${u.key}`,
        tenantId,
        key: u.key,
        value: u.value,
      })),
    ];

    const inputs = updates.map((u) => ({ tenantId, key: u.key, value: u.value }));

    try {
      await bulkSetFlags({
        variables: { inputs },
        optimisticResponse: {
          setTenantFeatureFlags: [
            {
              __typename: "Tenant",
              id: tenantId,
              tenantConfigs: finalConfigs,
            },
          ],
        },
      });
    } catch (err) {
      alerts.error(
        err instanceof Error ? err.message : "Failed to update feature flags. Please try again."
      );
    }
  };

  const handleToggle = (flagKey: FeatureFlagKey, nextValue: boolean | undefined) => {
    if (nextValue === undefined) return; // single-tenant mode never clears.

    const isOn = (k: FeatureFlagKey) => currentStates[k] === true;
    // Turning ON: auto-activate parents + `requires` deps that aren't
    //   currently on, so the new flag actually functions.
    // Turning OFF: auto-deactivate children + flags that `require` this
    //   one, so we never leave a dependent flag in the "inactive — needs …"
    //   state. Both directions are transitive.
    const updates: PendingUpdate[] = nextValue
      ? [
          { key: flagKey, value: true },
          ...collectDependencyKeysToActivate(flagKey, isOn, getMeta).map((k) => ({
            key: k,
            value: true,
          })),
        ]
      : [
          { key: flagKey, value: false },
          ...collectDependencyKeysToDeactivate(flagKey, isOn, getMeta).map((k) => ({
            key: k,
            value: false,
          })),
        ];

    // Measurements is expensive: toggling it (either direction) triggers
    // chemical aggregation across every active reporting year for this
    // tenant. Confirm before we write anything.
    const touchesMeasurements = updates.some(
      (u) =>
        u.key === FeatureFlagKey.Measurements &&
        (currentStates[FeatureFlagKey.Measurements] ?? false) !== u.value
    );

    if (touchesMeasurements) {
      setPendingConfirmation(updates);
    } else {
      // Fire-and-forget — `performToggles` surfaces errors via the
      // alerts provider, so the click handler doesn't need to await.
      void performToggles(updates);
    }
  };

  const confirmPending = () => {
    if (pendingConfirmation) {
      void performToggles(pendingConfirmation);
      setPendingConfirmation(null);
    }
  };

  const measurementsGoingOn =
    pendingConfirmation?.find((u) => u.key === FeatureFlagKey.Measurements)?.value === true;

  return (
    <>
      <FeatureFlagTogglePanel
        mode="single"
        currentStates={currentStates}
        onChange={handleToggle}
        disabled={loading}
      />

      <ConfirmDialog
        open={pendingConfirmation !== null}
        header={measurementsGoingOn ? "Enable Measurements?" : "Disable Measurements?"}
        confirmText={measurementsGoingOn ? "Enable" : "Disable"}
        onClose={() => setPendingConfirmation(null)}
        onConfirm={confirmPending}
      >
        <Stack spacing={1}>
          <Typography variant="body2">
            {measurementsGoingOn
              ? `Enabling the ${getMeta(FeatureFlagKey.Measurements).label} flag triggers chemical aggregation for this tenant across all active reporting years.`
              : `Disabling the ${getMeta(FeatureFlagKey.Measurements).label} flag will stop aggregation and hide measurement data from this tenant.`}
          </Typography>
          {pendingConfirmation &&
            (() => {
              const others = pendingConfirmation.filter(
                (u) => u.key !== FeatureFlagKey.Measurements
              );
              const alsoOn = others.filter((u) => u.value).map((u) => u.key);
              const alsoOff = others.filter((u) => !u.value).map((u) => u.key);
              const labelList = (keys: FeatureFlagKey[]) =>
                keys.map((k) => getMeta(k).label).join(", ");
              return (
                <>
                  {alsoOn.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      This will also turn on: {labelList(alsoOn)}.
                    </Typography>
                  )}
                  {alsoOff.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      This will also turn off: {labelList(alsoOff)}.
                    </Typography>
                  )}
                </>
              );
            })()}
        </Stack>
      </ConfirmDialog>
    </>
  );
}
